import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  FiAward,
  FiBell,
  FiCalendar,
  FiClock,
  FiTag,
  FiXCircle,
} from "react-icons/fi";
import { GET_BIDDING_AUCTION } from "../../constant";
import { GET } from "../../constant/apiMethods";
import Labels from "../../services/apiLabels";
import { API_ENDPOINT } from "../../services/api";
import { getAuth, getAuthUserId } from "../../services/localStorage";
import {
  resolveVehicleImageUrl,
  sanitizeImageUrl,
} from "../../utils/sanitizeImageUrl";
import {
  authHeaders,
  isApiSuccess,
  listFromPayload,
  pick,
  readJson,
  unwrapOffice,
  officeId,
} from "../../components/Offices/officeHelpers";
import {
  formatNextAppointment,
  nextAppointment,
} from "../../components/OfficeAppointments/appointmentHelpers";
import imgAuction from "../../assets/images/carauction.png";
import imgCarNew from "../../assets/images/carnew.jpg";
import imgAudi from "../../assets/images/audifront.jpg";
import imgElantra from "../../assets/images/elentra.jpg";
import imgSearchCar from "../../assets/images/searchcar.png";

const LIMIT = 10;
const SEEN_KEY = "dd_notif_seen_ids";
const PLACEHOLDER_IMAGES = [imgAuction, imgCarNew, imgAudi, imgElantra, imgSearchCar];
const API_PATHS = [
  `${Labels.notifications}?page_size=${LIMIT}`,
  `${Labels.notifications}?limit=${LIMIT}`,
];

function asList(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.notifications)) return value.notifications;
  if (Array.isArray(value?.data?.results)) return value.data.results;
  return [];
}

function toTime(value) {
  if (value == null || value === "") return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function relativeTime(value) {
  const t = toTime(value);
  if (!t) return "";
  const diff = Date.now() - t;
  const mins = Math.round(diff / 60000);
  if (Math.abs(mins) < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 14) return `${days}d ago`;
  return new Date(t).toLocaleDateString();
}

function readSeen() {
  try {
    const raw = JSON.parse(localStorage.getItem(SEEN_KEY) || "[]");
    return new Set(Array.isArray(raw) ? raw.map(String) : []);
  } catch {
    return new Set();
  }
}

function writeSeen(ids) {
  const next = Array.from(new Set(ids.map(String))).slice(-80);
  localStorage.setItem(SEEN_KEY, JSON.stringify(next));
}

function pickText(item, keys, fallback = "") {
  const value = pick(item, keys, fallback);
  if (value && typeof value === "object") {
    return pick(value, ["title", "name", "message", "text"], fallback);
  }
  return value == null ? fallback : String(value);
}

function hashIndex(value, size) {
  const text = String(value || "");
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash * 31 + text.charCodeAt(i)) % 100000;
  }
  return Math.abs(hash) % size;
}

function placeholderImage(seed) {
  return PLACEHOLDER_IMAGES[hashIndex(seed, PLACEHOLDER_IMAGES.length)];
}

function pickImage(item, seed = "") {
  if (!item || typeof item !== "object") {
    return placeholderImage(seed || "fallback");
  }
  const nested =
    item.product && typeof item.product === "object" ? item.product : null;
  const direct = [
    resolveVehicleImageUrl(item),
    resolveVehicleImageUrl(nested),
    sanitizeImageUrl(item.image_method),
    sanitizeImageUrl(item.thumbnail),
    sanitizeImageUrl(item.cover_image),
    sanitizeImageUrl(item.photo),
    sanitizeImageUrl(nested?.image_method),
    Array.isArray(item.exterior_images_)
      ? sanitizeImageUrl(item.exterior_images_[0]?.image)
      : "",
    Array.isArray(item.product_images)
      ? sanitizeImageUrl(
          item.product_images[0]?.image || item.product_images[0]?.url
        )
      : "",
  ].find(Boolean);
  return direct || placeholderImage(seed || pick(item, ["id", "pk", "product_id"], "car"));
}

function kindMeta(kind) {
  switch (kind) {
    case "won":
      return { label: "Won", Icon: FiAward, tone: "won" };
    case "lost":
      return { label: "Lost", Icon: FiXCircle, tone: "lost" };
    case "upcoming":
      return { label: "Upcoming", Icon: FiClock, tone: "upcoming" };
    case "active":
      return { label: "Active bid", Icon: FiTag, tone: "active" };
    case "appointment":
      return { label: "Inspection", Icon: FiCalendar, tone: "appointment" };
    default:
      return { label: "Update", Icon: FiBell, tone: "default" };
  }
}

function normalizeApiItem(item, index) {
  if (!item || typeof item !== "object") return null;
  const id = pick(item, ["id", "pk", "uuid"], `api-${index}`);
  const title = pickText(item, ["title", "heading", "subject", "type"], "Notification");
  const body = pickText(item, ["message", "body", "description", "text", "detail"], "");
  const createdAt = pick(item, [
    "created_at",
    "created",
    "date",
    "timestamp",
    "createdAt",
  ]);
  const href = pick(item, ["url", "link", "href"], "");
  const kindRaw = String(pick(item, ["kind", "category", "type"], "")).toLowerCase();
  let kind = "default";
  if (kindRaw.includes("won")) kind = "won";
  else if (kindRaw.includes("lost")) kind = "lost";
  else if (kindRaw.includes("appoint") || kindRaw.includes("inspect"))
    kind = "appointment";
  else if (kindRaw.includes("bid") || kindRaw.includes("auction")) kind = "active";
  return {
    id: String(id),
    title,
    body,
    createdAt,
    href: href || "",
    image: pickImage(item, String(id)),
    kind,
    unread: item.is_read === false || item.read === false || item.unread === true,
  };
}

function bidEvents(auction) {
  const groups = [
    ["active_auction", "Active bid", "active", "/dealers/dashbord/mybids"],
    ["upcoming_auction", "Upcoming auction", "upcoming", "/dealers/dashbord/mybids"],
    ["won_auction", "Auction won", "won", "/dealers/dashbord/mybids"],
    ["lost_auction", "Auction lost", "lost", "/dealers/dashbord/mybids"],
  ];
  const rows = [];
  groups.forEach(([key, title, kind, href]) => {
    asList(auction?.[key]).forEach((item, index) => {
      const vehicle = pickText(
        item,
        ["product_name", "vehicle", "name", "registration"],
        "Lot"
      );
      const specs = [
        pickText(item, ["brands", "brand_name"], ""),
        pickText(item, ["model_name"], ""),
      ]
        .filter(Boolean)
        .join(" · ");
      const createdAt = pick(item, [
        "bidOffer",
        "bid_on",
        "bid_date",
        "boughtOn",
        "bought_on",
        "purchase_date",
        "startIn",
        "start_in",
        "start_date",
        "created_at",
      ]);
      const id = pick(item, ["id", "pk", "product_id"], `${key}-${index}`);
      rows.push({
        id: `bid-${key}-${id}`,
        title,
        body: specs ? `${vehicle} · ${specs}` : vehicle,
        createdAt,
        href,
        image: pickImage(item, `bid-${key}-${id}`),
        kind,
        unread: false,
      });
    });
  });
  return rows;
}

function officeEvents(offices) {
  return offices
    .map((office, index) => {
      const next = nextAppointment(office);
      if (!next) return null;
      const id = officeId(office);
      const createdAt =
        pick(next, ["date_applied", "created_at", "date_selected"]) ||
        pick(next, ["date"]);
      return {
        id: `appt-${id}-${pick(next, ["id", "pk"], "next")}`,
        title: "Upcoming inspection",
        body: `${pick(office, ["Address", "address"], "Office")} · ${formatNextAppointment(
          office
        )}`,
        createdAt,
        href: id
          ? `/dealers/dashbord/offices/${id}/appointments`
          : "/dealers/dashbord/appointments",
        image: placeholderImage(`appt-${id || index}`),
        kind: "appointment",
        unread: false,
      };
    })
    .filter(Boolean);
}

function NotifyThumb({ item }) {
  const meta = kindMeta(item.kind);
  const Icon = meta.Icon;
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const src = failed ? placeholderImage(item.id) : item.image;

  useEffect(() => {
    setLoaded(false);
    setFailed(false);
  }, [item.image, item.id]);

  return (
    <span className={`dd-topbar__notify-thumb is-${meta.tone}`} aria-hidden="true">
      {!loaded ? <span className="dd-topbar__notify-shimmer" /> : null}
      <img
        key={src}
        src={src}
        alt=""
        loading="lazy"
        className={loaded ? "is-loaded" : ""}
        onLoad={() => setLoaded(true)}
        onError={() => {
          setFailed(true);
          setLoaded(false);
        }}
      />
      <span className="dd-topbar__notify-thumb-veil" />
      <span className={`dd-topbar__notify-thumb-badge is-${meta.tone}`}>
        <Icon size={11} />
      </span>
    </span>
  );
}

function NotifyRow({ item, index, isUnread, onNavigate }) {
  const meta = kindMeta(item.kind);
  const content = (
    <>
      <NotifyThumb item={item} />
      <span className="dd-topbar__notify-copy">
        <span className="dd-topbar__notify-row-top">
          <span className={`dd-topbar__notify-chip is-${meta.tone}`}>
            {meta.label}
          </span>
          {item.createdAt ? (
            <span className="dd-topbar__notify-time">
              {relativeTime(item.createdAt)}
            </span>
          ) : null}
        </span>
        <span className="dd-topbar__notify-title">{item.title}</span>
        {item.body ? (
          <span className="dd-topbar__notify-body">{item.body}</span>
        ) : null}
      </span>
      {isUnread ? <span className="dd-topbar__notify-dot" aria-hidden /> : null}
      <span className="dd-topbar__notify-shine" aria-hidden />
    </>
  );

  const className = `dd-topbar__notify-item${isUnread ? " is-unread" : ""}`;
  const style = { ["--dd-notify-i"]: index };

  if (item.href) {
    return (
      <Link
        to={item.href}
        role="menuitem"
        className={className}
        style={style}
        onClick={onNavigate}
      >
        {content}
      </Link>
    );
  }

  return (
    <div className={className} role="menuitem" style={style}>
      {content}
    </div>
  );
}

function NotifySkeleton() {
  return (
    <ul className="dd-topbar__notify-list dd-topbar__notify-list--skel" aria-hidden>
      {[0, 1, 2].map((i) => (
        <li key={i} style={{ ["--dd-notify-i"]: i }}>
          <div className="dd-topbar__notify-item is-skel">
            <span className="dd-topbar__notify-thumb">
              <span className="dd-topbar__notify-shimmer" />
            </span>
            <span className="dd-topbar__notify-copy">
              <span className="dd-topbar__notify-skel-line is-sm" />
              <span className="dd-topbar__notify-skel-line" />
              <span className="dd-topbar__notify-skel-line is-md" />
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function DashboardNotifications({ auction, getBiddingAuction, onOpen, accountOpen }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiItems, setApiItems] = useState(null);
  const [offices, setOffices] = useState([]);
  const [seen, setSeen] = useState(() => readSeen());
  const rootRef = useRef(null);
  const userId = getAuthUserId();

  const load = async ({ includeBids = false } = {}) => {
    const token = getAuth()?.token;
    if (includeBids && userId) getBiddingAuction(userId);
    setLoading(true);
    try {
      let fetchedApi = null;
      if (token) {
        for (const path of API_PATHS) {
          try {
            const response = await fetch(API_ENDPOINT(path), {
              headers: authHeaders(token),
            });
            if (response.status === 404) continue;
            const json = await readJson(response);
            if (!isApiSuccess(response, json) && response.status >= 400) continue;
            const list = asList(json).map(normalizeApiItem).filter(Boolean);
            if (list.length) {
              fetchedApi = list;
              break;
            }
          } catch {
            /* try next path */
          }
        }
        try {
          const response = await fetch(API_ENDPOINT(Labels.office_listing), {
            headers: authHeaders(token),
          });
          const json = await readJson(response);
          if (isApiSuccess(response, json)) {
            setOffices(listFromPayload(json).map((office) => unwrapOffice(office)));
          }
        } catch {
          setOffices([]);
        }
      }
      setApiItems(fetchedApi);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load({ includeBids: true });
    const timer = setInterval(() => load(), 60000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (accountOpen) setOpen(false);
  }, [accountOpen]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const items = useMemo(() => {
    const source =
      Array.isArray(apiItems) && apiItems.length
        ? apiItems
        : [...bidEvents(auction), ...officeEvents(offices)];
    return [...source]
      .sort((a, b) => toTime(b.createdAt) - toTime(a.createdAt))
      .slice(0, LIMIT);
  }, [apiItems, auction, offices]);

  const unreadCount = items.filter(
    (item) => item.unread || !seen.has(String(item.id))
  ).length;

  const toggle = () => {
    setOpen((wasOpen) => {
      const next = !wasOpen;
      if (next) {
        if (typeof onOpen === "function") onOpen();
        load({ includeBids: true });
        const merged = new Set(seen);
        items.forEach((item) => merged.add(String(item.id)));
        setSeen(merged);
        writeSeen(Array.from(merged));
      }
      return next;
    });
  };

  return (
    <div className="dd-topbar__notify" ref={rootRef}>
      <button
        type="button"
        className={`dd-topbar__notify-btn${open ? " is-open" : ""}${
          unreadCount ? " has-unread" : ""
        }`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Notifications"
        title="Notifications"
        onClick={toggle}
      >
        <FiBell size={18} aria-hidden />
        {unreadCount ? (
          <span className="dd-topbar__notify-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        ) : null}
      </button>
      {open ? (
        <div className="dd-topbar__notify-menu" role="menu">
          <div className="dd-topbar__notify-glow" aria-hidden="true" />
          <span className="dd-topbar__notify-orb dd-topbar__notify-orb--a" aria-hidden />
          <span className="dd-topbar__notify-orb dd-topbar__notify-orb--b" aria-hidden />
          <div className="dd-topbar__notify-head">
            <div>
              <p className="dd-topbar__notify-eyebrow">Dealer desk</p>
              <strong>Notifications</strong>
            </div>
            <span>{unreadCount ? `${unreadCount} new` : `Latest ${LIMIT}`}</span>
          </div>

          {loading && !items.length ? (
            <NotifySkeleton />
          ) : items.length ? (
            <ul className="dd-topbar__notify-list">
              {items.map((item, index) => (
                <li key={item.id}>
                  <NotifyRow
                    item={item}
                    index={index}
                    isUnread={item.unread || !seen.has(String(item.id))}
                    onNavigate={() => setOpen(false)}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <div className="dd-topbar__notify-empty">
              <span className="dd-topbar__notify-empty-media" aria-hidden>
                <img src={imgSearchCar} alt="" />
              </span>
              <p>No notifications yet</p>
              <span>Bids and office bookings will show up here.</span>
            </div>
          )}

          <div className="dd-topbar__notify-foot">
            <Link
              to="/dealers/dashbord/appointments"
              onClick={() => setOpen(false)}
            >
              View appointments
            </Link>
            <Link to="/dealers/dashbord/mybids" onClick={() => setOpen(false)}>
              View bids
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const mapStateToProps = (state) => ({
  auction: state.profile.auction,
});

const mapDispatchToProps = (dispatch) => ({
  getBiddingAuction: (id) =>
    dispatch({
      type: GET_BIDDING_AUCTION,
      payload: {
        type: GET_BIDDING_AUCTION,
        label: `${Labels.get_bidding_auction}${id}`,
        method: GET,
        hideOverlayLoader: true,
      },
    }),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DashboardNotifications);
